import React from 'react';
import Icon from '../../../components/AppIcon';

const TrustSignalsSection = () => {
  // Rename this conceptually to "Standards Referenced" (not certifications)
  const standardsReferenced = [
    {
      name: 'NFPA 921',
      description: 'Methodology aligned with recognized fire investigation principles',
      icon: 'Award',
    },
    {
      name: 'NFPA 1033',
      description: 'Investigator competency framework referenced for report structure',
      icon: 'CheckCircle',
    },
    {
      name: 'ASTM E2713',
      description: 'Forensic engineering and evidence documentation best practices',
      icon: 'ClipboardCheck',
    },
    {
      name: 'Daubert / FRCP 26',
      description: 'Reliability and disclosure framing where applicable',
      icon: 'Scale',
    },
  ];

  const securityFeatures = [
    {
      icon: 'Lock',
      title: 'Encrypted Data Transit',
      description: 'Modern HTTPS/TLS encryption for data in transit',
    },
    {
      icon: 'Shield',
      title: 'Encrypted Data Storage',
      description: 'Encryption at rest for stored files and records',
    },
    {
      icon: 'ShieldCheck',
      title: 'Mandatory 2FA',
      description: 'Multi-factor authentication required for all users',
    },
    {
      icon: 'Users',
      title: 'Role-Based Access',
      description: 'Access restricted by user role and case permissions',
    },
  ];

  // Replace unverified stats with feature highlights (no made-up numbers)
  const stats = [
    { value: 'AI Reports', label: 'Preliminary Analysis', icon: 'FileText' },
    { value: 'Image Markups', label: 'On Your Uploads', icon: 'Image' },
    { value: 'Clarifying Qs', label: 'Before Reporting', icon: 'HelpCircle' },
    { value: 'Expert Option', label: 'Human Review', icon: 'Users' },
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Built for Fire Case Decisions
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            A standards-informed, security-first workflow for generating preliminary fire forensics case intelligence from
            your uploaded materials.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-16">
          {stats?.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name={stat?.icon} size={24} className="text-primary" />
              </div>
              <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-1 md:mb-2">
                {stat?.value}
              </div>
              <div className="caption text-muted-foreground">{stat?.label}</div>
            </div>
          ))}
        </div>

        <div className="mb-16">
          <h3 className="text-xl md:text-2xl font-bold text-foreground text-center mb-8 md:mb-12">
            Standards Referenced
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {standardsReferenced?.map((item, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-xl p-6 text-center transition-smooth hover:shadow-elevation-md"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-success/10 rounded-full flex items-center justify-center">
                  <Icon name={item?.icon} size={32} className="text-success" />
                </div>
                <h4 className="font-semibold text-lg text-foreground mb-2">{item?.name}</h4>
                <p className="caption text-muted-foreground">{item?.description}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground max-w-3xl mx-auto">
            Note: References to standards are informational and reflect a methodology-aligned workflow. This platform is not
            affiliated with or endorsed by NFPA or any standards body.
          </p>
        </div>

        <div>
          <h3 className="text-xl md:text-2xl font-bold text-foreground text-center mb-8 md:mb-12">
            Enterprise Security
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {securityFeatures?.map((feature, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-xl p-6 transition-smooth hover:shadow-elevation-md"
              >
                <div className="w-12 h-12 mb-4 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={feature?.icon} size={24} className="text-primary" />
                </div>
                <h4 className="font-semibold text-base md:text-lg text-foreground mb-2">{feature?.title}</h4>
                <p className="text-sm text-muted-foreground">{feature?.description}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground max-w-3xl mx-auto">
            Security features described here reflect product design and implementation. No external security certification is
            implied.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TrustSignalsSection;
